<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { SmartPickerModal, type SmartPickerOption } from '@yuksalish/ui'
import { formatApiError } from '@yuksalish/api-client'
import { useProcurementApi } from '../composables/useProcurementApi'
import type { AgentOption } from '../types/procurement'

/**
 * Searchable agent picker for one role (`supplier` / `warehouse`).
 * Uses the agents API server-side search and pagination.
 */
const props = withDefaults(
  defineProps<{
    open: boolean
    /** Agent role to list (`supplier`, `warehouse`, …). */
    role: string
    title: string
    /** Id already selected, highlighted in the list. */
    selectedId?: number | null
  }>(),
  { selectedId: null },
)

const emit = defineEmits<{
  close: []
  select: [agent: AgentOption]
}>()

const { t } = useI18n()
const { listAgentOptions } = useProcurementApi()

const searchValue = ref('')
const items = ref<AgentOption[]>([])
const loading = ref(false)
const error = ref('')
const page = ref(1)
const totalPages = ref(1)

function toOptions(list: AgentOption[]): SmartPickerOption[] {
  return list.map((agent) => ({
    id: agent.id,
    title: agent.name,
    meta: agent.phone ?? '',
  }))
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const result = await listAgentOptions({
      role: props.role,
      search: searchValue.value.trim() || undefined,
      page: page.value,
      per_page: 20,
    })
    items.value = result.items
    totalPages.value = result.meta.total_pages
  } catch (cause) {
    error.value = formatApiError(cause)
    items.value = []
  } finally {
    loading.value = false
  }
}

function reset() {
  searchValue.value = ''
  page.value = 1
  error.value = ''
  items.value = []
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    reset()
    void load()
  },
)

watch(searchValue, () => {
  page.value = 1
  void load()
})

onMounted(() => {
  if (props.open) void load()
})
</script>

<template>
  <SmartPickerModal
    :open="open"
    :title="title"
    :eyebrow="t('procurement.picker.agentEyebrow')"
    :search-label="t('procurement.picker.agentSearchLabel')"
    :search-placeholder="t('procurement.picker.agentSearchPlaceholder')"
    :search-value="searchValue"
    :loading="loading"
    :error-message="error"
    :empty-message="t('procurement.picker.agentEmpty')"
    :items="toOptions(items)"
    :page="page"
    :total-pages="totalPages"
    :selected-id="selectedId"
    :page-label="t('procurement.picker.page', { page, total: Math.max(totalPages, 1) })"
    :close-aria-label="t('procurement.picker.close')"
    :retry-label="t('procurement.picker.retry')"
    :previous-label="t('procurement.picker.previous')"
    :next-label="t('procurement.picker.next')"
    :cancel-label="t('procurement.picker.cancel')"
    @close="emit('close')"
    @retry="load"
    @previous="page = Math.max(1, page - 1); load()"
    @next="page = Math.min(totalPages, page + 1); load()"
    @select="(option: SmartPickerOption) => {
      const match = items.find((agent) => String(agent.id) === String(option.id))
      if (match) emit('select', match)
    }"
    @update:search-value="(value: string) => (searchValue = value)"
  >
    <template #loading>{{ t('procurement.picker.agentLoading') }}</template>
  </SmartPickerModal>
</template>
